import React, { useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../config/authConfig';
import SEOHead from '../components/common/SEOHead';
import { ScreenshotFrame, Reveal, PhaseFlow, PublicHeader, PublicFooter } from '../components/common';
import { phases } from '../data/phases';
import './HowItWorks.css';

function HowItWorks() {
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();

  // Hash-scroll handler (PRD-171B WP8, A10). React Router does NOT scroll to a
  // hash natively, so a cross-page landing → /how-it-works#phase-NN jump (from the
  // <PhaseFlow> on the landing page) would otherwise dump the visitor at the top.
  // Depend on location.hash so a second click on a different box while already here
  // re-scrolls. Guard the lookup — a stale hash must not throw.
  //
  // On a client-side navigation the below-the-fold screenshots load lazily and grow
  // the page AFTER the first scroll, leaving a distant target (e.g. phase-06) a full
  // viewport short. Re-align a few times until the target settles at the top (bounded,
  // self-terminating: it stops once the pre-scroll offset is ~0 or after the cap).
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    let tries = 0;
    let timer;
    const align = () => {
      const el = document.getElementById(id);
      if (!el) return; // stale hash — never throw
      const offsetBefore = el.getBoundingClientRect().top;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      tries += 1;
      if (tries < 10 && Math.abs(offsetBefore) > 8) {
        timer = setTimeout(align, 200);
      }
    };
    align();
    return () => clearTimeout(timer);
  }, [location.hash]);

  const handleTrial = () => {
    if (process.env.NODE_ENV === 'development') {
      navigate('/command-center');
    } else {
      instance.loginRedirect({
        ...loginRequest,
        prompt: 'create',
      });
    }
  };

  // The six phases now live in ../data/phases.js so the detail cards below and
  // the shared <PhaseFlow> diagram cannot drift (PRD-171B WP8).

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "How DriftAlarm Monitors Your Attack Surface",
    "description": "Learn how DriftAlarm delivers External Attack Surface Management (EASM) through six assessment phases — from asset discovery to continuous drift detection.",
    "image": ["https://www.driftalarm.com/images/screenshots/command-center.png"],
    "datePublished": "2025-12-01T00:00:00-05:00",
    "dateModified": "2026-04-03T00:00:00-05:00",
    "author": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com" },
    "publisher": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com", "logo": { "@type": "ImageObject", "url": "https://www.driftalarm.com/images/og-image.png" } },
    "url": "https://www.driftalarm.com/how-it-works",
    "mainEntityOfPage": "https://www.driftalarm.com/how-it-works"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": "How It Works" }
    ]
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "The DriftAlarm Methodology",
    "description": "Six phases of attacker-perspective external attack surface assessment.",
    "step": phases.map((p, i) => ({
      "@type": "HowToStep",
      "position": i + 1,
      "name": p.title,
      "text": p.description,
      "url": `https://www.driftalarm.com/how-it-works#phase-${p.number}`,
    })),
  };

  return (
    <div className="how-it-works-page">
      <SEOHead
        title="How DriftAlarm Works | Attack Surface Monitoring Methodology"
        description="Six phases of attacker-perspective assessment — from asset discovery to AI-powered remediation roadmap. See how DriftAlarm monitors your external attack surface with real product screenshots."
        path="/how-it-works"
        jsonLd={[articleSchema, breadcrumbSchema, howToSchema]}
      />

      <PublicHeader activeLink="how-it-works" />

      {/* Hero Section */}
      <section className="hiw-hero">
        <div className="hiw-hero-content">
          <h1>The DriftAlarm Methodology</h1>
          <p className="hero-subtitle">
            How we help you get secure, and alert you when things change.
          </p>
          <div className="hero-ctas">
            <button className="da-btn da-btn--primary" onClick={handleTrial}>
              <span className="cta-shiny-text">Start Free Trial</span>
            </button>
            <Link to="/upgrade" className="da-btn da-btn--secondary">
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Phase Flow Diagram (left to right, same-page deep-links to each phase) */}
      <PhaseFlow />

      {/* Phases Section */}
      <section className="phases-section">
        <div className="phases-intro">
          <h2>SIX PHASES OF THE ASSESSMENT</h2>
        </div>

        <div className="phases-container">
          {phases.map((phase, index) => (
            <Reveal key={phase.number} id={`phase-${phase.number}`} className="phase-card-reveal" delay={index % 2 === 1 ? 240 : 0}>
            <div className={`phase-card ${index % 2 === 1 ? 'reverse' : ''}`}>
              <div className="phase-content">
                <div className="phase-header">
                  <span className="phase-number">{phase.number}</span>
                  <div className="phase-titles">
                    <h3>{phase.title}</h3>
                    <span className="phase-subtitle">{phase.subtitle}</span>
                  </div>
                </div>
                <p className="phase-description">{phase.description}</p>
                <ul className="phase-capabilities">
                  {phase.capabilities.map((cap, i) => (
                    <li key={i}>
                      <span className="cap-bullet">▹</span>
                      {cap}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="phase-visual">
                <ScreenshotFrame
                  src={phase.screenshot}
                  alt={phase.screenshotAlt}
                  size="full"
                  retina={phase.retina}
                />
                {phase.secondaryScreenshot && (
                  <div className="phase-secondary">
                    <ScreenshotFrame
                      src={phase.secondaryScreenshot}
                      alt={phase.secondaryAlt}
                      size="full"
                    />
                  </div>
                )}
              </div>
            </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Bonus Capabilities Section */}
      <section className="bonus-section">
        <h2>Beyond Scanning: A Complete Security Toolkit</h2>
        <div className="bonus-grid">
          <Reveal className="bonus-card-reveal" delay={0}>
          <div className="bonus-card">
            <ScreenshotFrame
              src="/images/screenshots/validate-test-selection"
              alt="Validation test picker showing XSS, SQLi, CORS, auth tests, and service tests"
              size="full"
            />
            <h3>Vulnerability Validation</h3>
            <p>
              Don't just detect — validate. Test for XSS reflection, SQL injection, CORS
              misconfiguration, subdomain takeover, anonymous database access, and more.
              Prove vulnerabilities are real before escalating.
            </p>
          </div>
          </Reveal>
          <Reveal className="bonus-card-reveal" delay={240}>
          <div className="bonus-card">
            <ScreenshotFrame
              src="/images/screenshots/pentesting-roadmap"
              alt="4-phase penetration testing roadmap from reconnaissance through exploitation"
              size="full"
            />
            <h3>Penetration Testing Roadmap</h3>
            <p>
              Each assessment generates a structured pentest roadmap — from completed
              reconnaissance through exploitation targets. Know exactly where a
              professional pentester would focus next.
            </p>
          </div>
          </Reveal>
          <Reveal className="bonus-card-reveal" delay={480}>
          <div className="bonus-card">
            <ScreenshotFrame
              src="/images/screenshots/tools-console"
              alt="Built-in security tools — Curl, Headers, SSL/TLS, DNS, WHOIS, Port Check, Subdomains, Cloud Enum"
              size="full"
            />
            <h3>Built-In Security Tools</h3>
            <p>
              Curl, Headers analysis, SSL/TLS inspection, DNS lookup, WHOIS, Port Check,
              Subdomain enumeration, Cloud Enum, and Encoder — all from your browser.
              No terminal required.
            </p>
          </div>
          </Reveal>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <Reveal className="cta-content">
          <h2>Ready to See Your Attack Surface?</h2>
          <p>
            Add your first domain. Get results in 90 seconds. Full Standard access for 30 days.
          </p>
          <div className="cta-buttons">
            <button className="da-btn da-btn--primary" onClick={handleTrial}>
              <span className="cta-shiny-text">Start Your Free Trial →</span>
            </button>
          </div>
          <p className="cta-trust">No credit card &bull; No sales call &bull; Cancel anytime</p>
        </Reveal>
      </section>

      {/* Footer (unified style) */}
      <PublicFooter />
    </div>
  );
}

export default HowItWorks;
